import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { formatMoneyFromCents } from "../../../../helpers/money";
import { getInventoryValuationReport } from "../../../../services/productService";
import { getUnits, Unit } from "../../../../services/unitService";
import { InventoryValuationReport } from "../../../../types/inventoryBatch";

const UNITS_PAGE_SIZE = 100;

const InventoryValuationPage = () => {
  const { t, i18n } = useTranslation();
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [report, setReport] = useState<InventoryValuationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(true);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    const loadUnits = async () => {
      setLoadingUnits(true);
      try {
        const response = await getUnits({ per_page: UNITS_PAGE_SIZE });
        const list = response.data ?? [];
        setUnits(list);
        if (list.length === 1) {
          setUnitId(String(list[0].id));
        }
      } catch {
        toast.error(t("studio.products.valuation.toast.unitsError"));
      } finally {
        setLoadingUnits(false);
      }
    };

    void loadUnits();
  }, [t]);

  const loadReport = useCallback(async () => {
    if (!unitId) {
      setReport(null);
      return;
    }

    setLoading(true);
    try {
      const response = await getInventoryValuationReport(Number(unitId));
      setReport(response.data);
    } catch (error: any) {
      setReport(null);
      toast.error(error?.data || t("studio.products.valuation.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t, unitId]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  document.title = t("studio.products.valuation.documentTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("studio.products.valuation.title")}
          pageTitle={t("menu.catalog.products")}
        />
        <ToastContainer />

        <Card>
          <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
            <div>
              <h5 className="card-title mb-1">{t("studio.products.valuation.title")}</h5>
              <p className="text-muted mb-0">{t("studio.products.valuation.subtitle")}</p>
            </div>
            <Button color="light" tag={Link} to="/products">
              {t("studio.products.valuation.actions.backToProducts")}
            </Button>
          </CardHeader>
          <CardBody>
            <Row className="g-3 mb-4">
              <Col md={4}>
                <Label htmlFor="valuation-unit">{t("studio.products.valuation.fields.unit")}</Label>
                <Input
                  id="valuation-unit"
                  type="select"
                  value={unitId}
                  disabled={loadingUnits}
                  onChange={(event) => setUnitId(event.target.value)}
                >
                  <option value="">{t("studio.products.valuation.fields.unitPlaceholder")}</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            {!unitId ? (
              <p className="text-muted mb-0">{t("studio.products.valuation.empty.selectUnit")}</p>
            ) : loading ? (
              <div className="text-center py-5">
                <Spinner />
              </div>
            ) : !report || report.products.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted mb-3">{t("studio.products.valuation.empty.noData")}</p>
                <Button color="primary" tag={Link} to="/products">
                  {t("studio.products.valuation.empty.ctaProducts")}
                </Button>
              </div>
            ) : report.total_cents === 0 ? (
              <>
                <div className="alert alert-warning mb-3" role="status">
                  <p className="mb-2">{t("studio.products.valuation.empty.noCost")}</p>
                  <Button color="warning" outline size="sm" tag={Link} to="/products">
                    {t("studio.products.valuation.empty.ctaSetCost")}
                  </Button>
                </div>
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("studio.products.valuation.table.product")}</th>
                        <th>{t("studio.products.valuation.table.quantity")}</th>
                        <th>{t("studio.products.valuation.table.avgCost")}</th>
                        <th>{t("studio.products.valuation.table.total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.products.map((row) => (
                        <tr key={row.product_id}>
                          <td>{row.product_name}</td>
                          <td>{row.quantity}</td>
                          <td>{formatMoney(row.avg_cost_cents)}</td>
                          <td>{formatMoney(row.total_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <span className="fw-semibold me-3">
                    {t("studio.products.valuation.summary.totalValue", {
                      value: formatMoney(report.total_cents),
                    })}
                  </span>
                  <span className="text-muted">
                    {t("studio.products.valuation.summary.totalQty", {
                      qty: report.total_quantity,
                    })}
                  </span>
                </div>
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("studio.products.valuation.table.product")}</th>
                        <th>{t("studio.products.valuation.table.quantity")}</th>
                        <th>{t("studio.products.valuation.table.avgCost")}</th>
                        <th>{t("studio.products.valuation.table.total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.products.map((row) => (
                        <tr key={row.product_id}>
                          <td>{row.product_name}</td>
                          <td>{row.quantity}</td>
                          <td>{formatMoney(row.avg_cost_cents)}</td>
                          <td>{formatMoney(row.total_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default InventoryValuationPage;
